import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useGetAllCollectionsLazyQuery  } from '@api/graphql-codegen';
import { SelectOption } from "../../../types";
import styles from "./MultiCollections.module.scss";
import Header from "../../../components/header/Header";
import Button from "../../../components/button/Button";
import Footer from "../../../components/footer/Footer";
import CollectionFilterLayout from "@src/components/collection/FilterLayout";
import { useToast } from "@src/hooks";
import GradeItem from "./gradeItem/GradeItem";
import MultiCollectionModal from "./modal/MultiCollectionModal";
import DeleteModal from "@src/components/modal/delete-modal/DeleteModal";
import { useHistory, useLocation } from "react-router-dom";
import { useDeleteMultiCollectionMutation, useGetListNormalMultiCollectionsLazyQuery } from "@src/api/graphql-codegen";
import Spinner from "@src/components/spinner/Spinner";
import { logger } from '../../../utils/logger';
import FooterPage from '@src/components/footer-page/FooterPage';

type Props = {};

function matchesSelectedGrade(gradeEntry: string, selectedGrade: string): boolean {
  // Direct match
  if (gradeEntry === selectedGrade) return true;

  const selectedMatch = selectedGrade.match(/(Grade|Year) (\d+)/);
  const entryRangeMatch = gradeEntry.match(/(Grade|Year) (\d+)-(\d+)/);

  if (selectedMatch && entryRangeMatch) {
    const [, selectedType, selectedNumStr] = selectedMatch;
    const [, entryType, startStr, endStr] = entryRangeMatch;

    if (selectedType === entryType) {
      const selectedNum = Number(selectedNumStr);
      const start = Number(startStr);
      const end = Number(endStr);
      return selectedNum >= start && selectedNum <= end;
    }
  }

  return false;
}

function MultiCollections(props: Props) {
  const [keyword, setKeyword] = useState('');
  const [ageGroup, handleChangeAgeGroup] = useState('');
  const [country, setCountry] = useState<string>('');
  const [selCollection, setSelCollection] = useState<any>();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selected, setSelected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const history = useHistory();
  const location = useLocation();
  const id = location.state?.id;

  const { successToast, errorToast } = useToast();

  const [getCollectionList, {data, loading, refetch}] = useGetListNormalMultiCollectionsLazyQuery({
    variables: { input: { keyword: '', ageGroup: '', country: '' }},
    fetchPolicy: 'network-only'
  })

  const [deleteMultiCollection, {loading: removing}] = useDeleteMultiCollectionMutation({
    onCompleted(data, clientOptions) {
      if (data.deleteMultiCollection) {
        successToast("Multi Collection has been removed successfully!");
        refetch();
        setTimeout(() => setShowDeleteModal(false), 100)
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      errorToast("Remove failed, Please try again!")
    }
  })

  useEffect(() => {
    getCollectionList();
  }, [getCollectionList])

  useEffect(() => {
    if (id) {
      setSelectedId(id);
      setSelected(true);
      setTimeout(() => {
        const courseElement = document.getElementById(id);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 1000)
    }
  }, [id])

  const collections = useMemo(() => {
    var filteredCollections = [...(data?.listNormalMultiCollections ?? [])].sort((a, b) => (a?.name ?? '').localeCompare((b?.name ?? '')));

    if (keyword) {
      filteredCollections = filteredCollections.filter(collection => collection?.name.toLocaleLowerCase().includes(keyword.toLocaleLowerCase()))
    }
    if (country) {
      filteredCollections = filteredCollections.filter(collection => collection?.country.includes(country))
    }
    if (ageGroup) {
      filteredCollections = filteredCollections.filter(collection => {
        let gradeArray: string[] = [];

        if (!collection?.grade) return false;

        try {
          gradeArray = JSON.parse(collection?.grade);
          if (!Array.isArray(gradeArray)) {
            logger.error('grade is not an array after parsing:', collection.grade);
            return false;
          }
        } catch (e) {
          if (collection.grade === ageGroup) return true;
          logger.error('Invalid JSON in grade:', collection.grade);
          return false;
        }
        return gradeArray.some(gradeEntry =>
          matchesSelectedGrade(gradeEntry, ageGroup)
        );
      });
    }
    return filteredCollections;
  }, [keyword, country, ageGroup, data?.listNormalMultiCollections])

  const handleChangeSearch = useCallback((v) => {
    setKeyword(v);
  }, []);

  const handleResetFilters = useCallback(() => {
    setCountry('');
    setKeyword('');
    handleChangeAgeGroup('');
  }, []);

  const handleChangeCountry = useCallback((option: SelectOption) => {
    setCountry(option.value);
  },[])

  const handleCreateCollection = () => {
    setSelCollection(undefined);
    setShowModal(true);
  }

  const handleRemoveCollection = useCallback(() => {
    if (selCollection) {
      deleteMultiCollection({
        variables: {
          input: {
            id: selCollection.id
          }
        }
      })  
    }
    
  }, [selCollection, deleteMultiCollection])

  const handleEditCollection = (grade: any) => {
    setSelCollection(grade);
    setShowModal(true);
  }

  const handleCustomize = (grade: any) => {
    history.push(`/super/multi-collections/customize/${grade.id}`);
  }

  const handleSelect = useCallback((gradeId: string) => {
    if (selectedId === gradeId) {
      setSelected(false);
      setSelectedId(null);
    } else {
      setSelected(true);
      setSelectedId(gradeId);
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }, [selectedId, setSelected, setSelectedId])

  const handleDeleteCollection = (grade: any) => {
    setSelCollection(grade);
    setShowDeleteModal(true);
  }

  const handleClose = useCallback(() => {
    setShowModal(false);
    refetch();
  }, [refetch])

  return (
    <>
      <Header />
      <div className={styles.page}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div>
              <h2 className={styles.title}>Multi-Collection Management</h2>
            </div>
            <div className={styles.rightHeader}>
              <div className={styles.search}></div>
              <Button
                onClick={handleCreateCollection}
                className={styles.addBtn}
                label={"Create Multi-Collection"}
                leftIcon={<div className={styles.plus}>+</div>}
              />
            </div>
          </div>
          {loading ? <div className={styles.spinner}><Spinner/></div> : <>
            <div className={styles.content}>
              <CollectionFilterLayout
                keyword={keyword}                
                country={country}
                ageGroup={ageGroup}
                handleChangeAgeGroup={handleChangeAgeGroup}
                handleChangeSearch={handleChangeSearch}
                handleChangeCountry={handleChangeCountry}
                handleResetFilters={handleResetFilters}
              />
            </div>
            <div className={styles.collectionContainer}>
              <div className={styles.collectionCardsContainer}>
                {collections.map((item, index) => {
                  return (
                    <div id={item?.id} key={index} style={{position: 'relative'}}>
                      <GradeItem
                        selected={selected}
                        selectedId={selectedId}
                        gradeId={item?.id ?? ''}
                        grade={item}
                        handleSeleted={handleSelect}
                        handleEdit={handleEditCollection}
                        handleDelete={handleDeleteCollection}
                        handleCustomize={handleCustomize}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </>}
        </div>
        <FooterPage />
      </div>
      {/* <Footer /> */}
      {showModal && <MultiCollectionModal open={showModal} onClose={handleClose} collection={selCollection} />}
      {showDeleteModal && <DeleteModal open={showDeleteModal} onClose={() => {setShowDeleteModal(false)}} loading={removing} onDelete={handleRemoveCollection} message={`Are you sure you want to delete ${selCollection?.name}?`} cancelLabel={'NO'} deleteLabel={'DELETE'}/>}
    </>
  );
}

export default MultiCollections;
